import { isPasswordStrongEnough } from '../../../helpers/passwordStrength'
import { EMAIL_REGEX } from '../../../hooks/customer-auth-form/validation.constants'
import type { ActivationFlowModel, RequirementTone } from './ActivationFlow.types'
import type { ActivationValidationModel, Translate } from './activationViewModel.types'

const passwordRequirementTone = ({
  met,
  showError,
}: {
  met: boolean
  showError: boolean
}): RequirementTone => {
  if (met) return 'met'
  if (showError) return 'error'
  return 'unmet'
}

export const resolveActivationValidationModel = ({
  state,
  t,
}: {
  state: ActivationFlowModel
  t: Translate
}): ActivationValidationModel => {
  const hasPasswordMinLength = state.setup.password.trim().length >= 8
  const hasPasswordLetter = /[A-Za-z]/.test(state.setup.password)
  const hasPasswordNumber = /\d/.test(state.setup.password)
  const passwordsMatch =
    Boolean(state.setup.confirmPassword) && state.setup.confirmPassword === state.setup.password
  const passwordReady = hasPasswordMinLength && hasPasswordLetter && hasPasswordNumber && passwordsMatch

  const passwordInvalidMessage = !state.setup.password.trim()
    ? t('customer.auth.validation.requiredField')
    : !isPasswordStrongEnough(state.setup.password)
      ? t('customer.auth.validation.passwordWeak')
      : ''
  const passwordErrorVisible =
    (state.setup.passwordTouched && state.setup.passwordDirty && Boolean(passwordInvalidMessage)) ||
    (state.setup.formSubmitted && Boolean(passwordInvalidMessage))
  const passwordError = passwordErrorVisible ? passwordInvalidMessage : ''

  const confirmPasswordInvalidMessage = !state.setup.confirmPassword.trim()
    ? t('customer.auth.validation.requiredField')
    : state.setup.confirmPassword !== state.setup.password
      ? t('customer.auth.validation.passwordMismatch')
      : ''
  const confirmPasswordErrorVisible =
    (state.setup.confirmPasswordTouched && state.setup.confirmPasswordDirty && Boolean(confirmPasswordInvalidMessage)) ||
    (state.setup.formSubmitted && Boolean(confirmPasswordInvalidMessage))
  const confirmPasswordError = confirmPasswordErrorVisible ? confirmPasswordInvalidMessage : ''

  const fieldErrors = {
    password: passwordError,
    confirmPassword: confirmPasswordError,
  }

  const isRecoveryState =
    state.status === 'recovery' || state.status === 'expired' || state.status === 'invalid'
  const normalizedRecoveryEmail = state.recovery.email.trim().toLowerCase()
  const recoveryEmailValid = EMAIL_REGEX.test(normalizedRecoveryEmail)
  const activationFieldsValid = Boolean(state.token) && passwordReady

  const cardStateKey = state.status === 'success'
    ? 'activate-success'
    : state.status === 'loading'
      ? 'activate-loading'
      : isRecoveryState
        ? state.recovery.success
          ? 'recovery-success'
          : 'recovery-form'
        : 'activate-form'

  const recoveryEmailError = (() => {
    if (!state.recovery.emailTouched && !state.recovery.submitAttempted) return ''
    if (!normalizedRecoveryEmail) return t('customer.auth.validation.requiredField')
    if (!recoveryEmailValid) return t('customer.auth.validation.emailInvalid')
    return ''
  })()

  const passwordRequirements = [
    {
      label: t('customer.auth.activate.requirements.length'),
      tone: passwordRequirementTone({
        met: hasPasswordMinLength,
        showError: (state.setup.passwordTouched || state.setup.formSubmitted) && !hasPasswordMinLength,
      }),
    },
    {
      label: t('customer.auth.activate.requirements.letter'),
      tone: passwordRequirementTone({
        met: hasPasswordLetter,
        showError: (state.setup.passwordTouched || state.setup.formSubmitted) && !hasPasswordLetter,
      }),
    },
    {
      label: t('customer.auth.activate.requirements.number'),
      tone: passwordRequirementTone({
        met: hasPasswordNumber,
        showError: (state.setup.passwordTouched || state.setup.formSubmitted) && !hasPasswordNumber,
      }),
    },
    {
      label: t('customer.auth.activate.requirements.match'),
      tone: passwordRequirementTone({
        met: passwordsMatch,
        showError:
          (state.setup.confirmPasswordTouched || state.setup.formSubmitted) && !passwordsMatch,
      }),
    },
  ]

  const setupIntroChecklistVisible =
    !isRecoveryState &&
    (state.status === 'setup' || state.status === 'loading' || state.status === 'success')

  return {
    passwordReady,
    activationFieldsValid,
    isRecoveryState,
    normalizedRecoveryEmail,
    recoveryEmailValid,
    recoveryEmailError,
    cardStateKey,
    setupIntroChecklistVisible,
    passwordRequirements,
    fieldErrors,
  }
}
